// src/pages/admin/Analytics.js
import React, { useEffect, useState } from 'react';
import api from '../../utils/api';

const fmtCOP = v => new Intl.NumberFormat('es-CO', { style:'currency', currency:'COP', minimumFractionDigits:0 }).format(v || 0);

const PAGINA_LABEL = (p) => {
  if (!p || p === '/') return 'Inicio';
  const map = {
    '/carrito': 'Carrito', '/checkout': 'Checkout', '/pedido-ok': 'Pedido confirmado',
    '/nuestra-historia': 'Nuestra historia', '/compromiso-ambiental': 'Compromiso ambiental',
    '/sistema-refill': 'Sistema Refill', '/contacto': 'Contacto', '/noticias': 'Noticias',
    '/mi-cuenta': 'Mi cuenta', '/pqrs': 'PQRs',
  };
  if (map[p]) return map[p];
  if (p.startsWith('/producto/')) return 'Producto: ' + p.replace('/producto/', '');
  if (p.startsWith('/noticias/')) return 'Noticia: ' + p.replace('/noticias/', '');
  return p;
};

const PAIS_NOMBRE = (codigo) => {
  if (!codigo) return 'Desconocido';
  try {
    return new Intl.DisplayNames(['es'], { type: 'region' }).of(codigo) || codigo;
  } catch (e) { return codigo; }
};

const DISPOSITIVO_ICONO = (tipo) => {
  if (tipo === 'mobile') return '📱';
  if (tipo === 'tablet') return '📟';
  return '💻';
};

const DISPOSITIVO_LABEL = (tipo) => {
  if (tipo === 'mobile') return 'Móvil';
  if (tipo === 'tablet') return 'Tablet';
  return 'Escritorio';
};

const FUENTE_LABEL = (f) => {
  const map = { directo:'Directo', google:'Google', meta:'Meta (FB/IG)', whatsapp:'WhatsApp', tiktok:'TikTok', interno:'Interno (wawita.co)' };
  return map[f] || f;
};

const fmtTiempo = (seg) => {
  if (seg === null || seg === undefined) return '—';
  if (seg < 60) return `${seg}s`;
  return `${Math.floor(seg/60)}m ${seg%60}s`;
};

export default function Analytics() {
  const [dias, setDias] = useState(7);
  const [resumen, setResumen] = useState(null);
  const [paginas, setPaginas] = useState([]);
  const [productos, setProductos] = useState([]);
  const [sinConversion, setSinConversion] = useState([]);
  const [embudo, setEmbudo] = useState([]);
  const [abandono, setAbandono] = useState({ porProducto:[], porEtapa:[] });
  const [horarios, setHorarios] = useState([]);
  const [demografia, setDemografia] = useState({ paises:[], ciudades:[], dispositivos:[], navegadores:[], so:[] });
  const [origenes, setOrigenes] = useState({ fuentes:[], campanas:[] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const q = `?dias=${dias}`;
    Promise.all([
      api.get('/analytics/resumen' + q).then(r => setResumen(r.data)).catch(() => setResumen(null)),
      api.get('/analytics/paginas' + q).then(r => setPaginas(r.data)).catch(() => setPaginas([])),
      api.get('/analytics/productos' + q).then(r => setProductos(r.data)).catch(() => setProductos([])),
      api.get('/analytics/productos-sin-conversion' + q).then(r => setSinConversion(r.data)).catch(() => setSinConversion([])),
      api.get('/analytics/embudo' + q).then(r => setEmbudo(r.data)).catch(() => setEmbudo([])),
      api.get('/analytics/abandono' + q).then(r => setAbandono(r.data)).catch(() => setAbandono({ porProducto:[], porEtapa:[] })),
      api.get('/analytics/horarios' + q).then(r => setHorarios(r.data)).catch(() => setHorarios([])),
      api.get('/analytics/demografia' + q).then(r => setDemografia(r.data)).catch(() => setDemografia({ paises:[], ciudades:[], dispositivos:[], navegadores:[], so:[] })),
      api.get('/analytics/origenes' + q).then(r => setOrigenes(r.data)).catch(() => setOrigenes({ fuentes:[], campanas:[] })),
    ]).finally(() => setLoading(false));
  }, [dias]);

  const maxEmbudo = embudo.length > 0 ? embudo[0].sesiones : 1;
  const maxHora = Math.max(1, ...horarios.map(h => h.eventos));

  return (
    <div>
      {/* Topbar */}
      <div style={{ background:'#fff', borderBottom:'1px solid var(--border)', padding:'14px 28px', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, zIndex:10, flexWrap:'wrap', gap:12 }}>
        <div>
          <h1 style={{ fontSize:18, fontWeight:900, color:'var(--forest)' }}>Analytics</h1>
          <div style={{ fontSize:12, color:'var(--text-muted)', marginTop:2 }}>Comportamiento de visitantes y conversión</div>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          {[7, 15, 30, 90].map(d => (
            <button key={d} onClick={() => setDias(d)}
              style={{ padding:'7px 16px', borderRadius:8, border:'1.5px solid var(--border)', fontSize:12, fontWeight:700, cursor:'pointer',
                background: dias===d ? 'var(--forest)' : '#fff', color: dias===d ? '#9FBE6A' : 'var(--forest)' }}>
              {d} días
            </button>
          ))}
        </div>
      </div>

      <div style={{ padding:'24px 28px' }}>
        {loading && <div style={{ textAlign:'center', padding:40, color:'var(--text-muted)' }}>Cargando datos...</div>}

        {!loading && (
        <>
        {/* ── Métricas resumen ── */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16, marginBottom:24 }}>
          <div className="metric-card">
            <div className="metric-label">Sesiones</div>
            <div className="metric-value">{resumen?.sesiones || 0}</div>
            <div className="metric-sub" style={{ color:'var(--text-muted)' }}>{resumen?.pageviews || 0} vistas de página</div>
          </div>
          <div className="metric-card">
            <div className="metric-label">Conversión global</div>
            <div className="metric-value">{resumen?.conversionGlobal || 0}%</div>
            <div className="metric-sub" style={{ color:'var(--text-muted)' }}>sesiones → compra</div>
          </div>
          <div className="metric-card">
            <div className="metric-label">Ticket promedio</div>
            <div className="metric-value">{fmtCOP(resumen?.ticketPromedio)}</div>
            <div className="metric-sub" style={{ color:'var(--text-muted)' }}>{resumen?.compras || 0} compras · {fmtCOP(resumen?.ingresos)}</div>
          </div>
          <div className="metric-card">
            <div className="metric-label">Abandono de carrito</div>
            <div className="metric-value" style={{ color: (resumen?.tasaAbandonoCarrito||0) > 50 ? 'var(--red)' : 'var(--forest)' }}>{resumen?.tasaAbandonoCarrito || 0}%</div>
            <div className="metric-sub" style={{ color:'var(--text-muted)' }}>{resumen?.addCarrito || 0} agregaron al carrito</div>
          </div>
        </div>

        {/* ── Embudo de conversión ── */}
        <div className="card" style={{ marginBottom:24 }}>
          <div className="card-header"><span className="card-title">Embudo de conversión</span></div>
          <div className="card-body">
            {embudo.map((e, i) => (
              <div key={e.etapa} style={{ marginBottom: i < embudo.length-1 ? 16 : 0 }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6, fontSize:13 }}>
                  <span style={{ fontWeight:700, color:'var(--forest)' }}>{e.etapa}</span>
                  <span style={{ color:'var(--text-muted)' }}>
                    <strong style={{ color:'var(--forest)', fontSize:14 }}>{e.sesiones}</strong> sesiones
                    {i > 0 && <span style={{ marginLeft:8, fontWeight:700, color: e.pct_anterior >= 50 ? 'var(--green)' : 'var(--red)' }}>({e.pct_anterior}%)</span>}
                  </span>
                </div>
                <div style={{ height:24, background:'rgba(28,58,30,0.06)', borderRadius:8, overflow:'hidden' }}>
                  <div style={{
                    height:'100%', borderRadius:8,
                    width: maxEmbudo > 0 ? `${(e.sesiones / maxEmbudo) * 100}%` : '0%',
                    background: 'linear-gradient(90deg, #9FBE6A, #2c5e2e)',
                    transition:'width 0.3s'
                  }} />
                </div>
              </div>
            ))}
            {embudo.length === 0 && <div style={{ color:'var(--text-muted)', fontSize:13, textAlign:'center', padding:20 }}>Sin datos suficientes en este rango</div>}
          </div>
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, marginBottom:24 }}>
          {/* ── Páginas más visitadas ── */}
          <div className="card">
            <div className="card-header"><span className="card-title">Páginas más visitadas</span></div>
            <div className="card-body" style={{ padding:0 }}>
              {paginas.length === 0 ? (
                <div style={{ padding:20, textAlign:'center', color:'var(--text-muted)', fontSize:13 }}>Sin datos</div>
              ) : (
                <table className="table" style={{ fontSize:13 }}>
                  <thead><tr><th>Página</th><th>Visitas</th><th>T. promedio</th></tr></thead>
                  <tbody>
                    {paginas.map(p => (
                      <tr key={p.pagina}>
                        <td style={{ fontWeight:600 }}>{PAGINA_LABEL(p.pagina)}</td>
                        <td style={{ textAlign:'center', fontWeight:700, color:'var(--green)' }}>{p.visitas}</td>
                        <td style={{ textAlign:'center', color:'var(--text-muted)' }}>{fmtTiempo(p.tiempo_promedio_seg)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* ── Horarios pico ── */}
          <div className="card">
            <div className="card-header"><span className="card-title">Horarios de mayor tráfico</span></div>
            <div className="card-body">
              {horarios.length === 0 ? (
                <div style={{ padding:20, textAlign:'center', color:'var(--text-muted)', fontSize:13 }}>Sin datos</div>
              ) : (
                <div style={{ display:'flex', alignItems:'flex-end', gap:3, height:140 }}>
                  {Array.from({length:24}, (_, h) => horarios.find(x => x.hora === h) || { hora:h, eventos:0, compras:0 }).map(h => (
                    <div key={h.hora} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'flex-end', height:'100%' }} title={`${h.hora}:00 — ${h.eventos} eventos, ${h.compras} compras`}>
                      <div style={{
                        width:'100%', borderRadius:'3px 3px 0 0',
                        height: `${Math.max(2, (h.eventos / maxHora) * 100)}%`,
                        background: h.compras > 0 ? 'linear-gradient(180deg, #9FBE6A, #2c5e2e)' : 'rgba(28,58,30,0.12)',
                      }} />
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:10, color:'var(--text-muted)', marginTop:6 }}>
                <span>0h</span><span>6h</span><span>12h</span><span>18h</span><span>23h</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Productos más seleccionados ── */}
        <div className="card" style={{ marginBottom:24 }}>
          <div className="card-header"><span className="card-title">Productos más seleccionados</span></div>
          <div className="card-body" style={{ padding:0 }}>
            {productos.length === 0 ? (
              <div style={{ padding:20, textAlign:'center', color:'var(--text-muted)', fontSize:13 }}>Sin datos</div>
            ) : (
              <table className="table" style={{ fontSize:13 }}>
                <thead><tr><th>Producto</th><th>Vistas</th><th>Ver detalles</th><th>Add. carrito</th><th>Comprar ahora</th><th>Compras</th><th>CTR</th></tr></thead>
                <tbody>
                  {productos.slice(0, 15).map(p => (
                    <tr key={p.producto_id}>
                      <td style={{ fontWeight:600 }}>{p.producto_nombre || p.producto_id}</td>
                      <td style={{ textAlign:'center' }}>{p.vistas}</td>
                      <td style={{ textAlign:'center' }}>{p.ver_detalles}</td>
                      <td style={{ textAlign:'center', fontWeight:700, color:'var(--green)' }}>{p.add_carrito}</td>
                      <td style={{ textAlign:'center' }}>{p.comprar_ahora}</td>
                      <td style={{ textAlign:'center', fontWeight:700 }}>{p.compras}</td>
                      <td style={{ textAlign:'center', fontWeight:700, color: p.ctr >= 20 ? 'var(--green)' : 'var(--text-muted)' }}>{p.ctr}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, marginBottom:24 }}>
          {/* ── Abandono por etapa ── */}
          <div className="card">
            <div className="card-header"><span className="card-title">Abandono por etapa del checkout</span></div>
            <div className="card-body" style={{ padding:0 }}>
              {abandono.porEtapa.length === 0 || abandono.porEtapa[0]?.sesiones === 0 ? (
                <div style={{ padding:20, textAlign:'center', color:'var(--text-muted)', fontSize:13 }}>Sin datos</div>
              ) : (
                <table className="table" style={{ fontSize:13 }}>
                  <thead><tr><th>Etapa</th><th>Sesiones</th><th>Abandonos</th></tr></thead>
                  <tbody>
                    {abandono.porEtapa.map(e => (
                      <tr key={e.etapa}>
                        <td style={{ fontWeight:600 }}>{e.etapa}</td>
                        <td style={{ textAlign:'center', fontWeight:700, color:'var(--green)' }}>{e.sesiones}</td>
                        <td style={{ textAlign:'center', fontWeight:700, color: e.abandonos > 0 ? 'var(--red)' : 'var(--text-muted)' }}>{e.abandonos ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* ── Productos vistos pero nunca agregados ── */}
          <div className="card">
            <div className="card-header"><span className="card-title">Productos vistos sin conversión</span></div>
            <div className="card-body" style={{ padding:0 }}>
              {sinConversion.length === 0 ? (
                <div style={{ padding:20, textAlign:'center', color:'var(--text-muted)', fontSize:13 }}>Todos los productos vistos generaron al menos un "agregar al carrito" 🎉</div>
              ) : (
                <table className="table" style={{ fontSize:13 }}>
                  <thead><tr><th>Producto</th><th>Vistas</th></tr></thead>
                  <tbody>
                    {sinConversion.map(p => (
                      <tr key={p.producto_id}>
                        <td style={{ fontWeight:600 }}>{p.producto_nombre || p.producto_id}</td>
                        <td style={{ textAlign:'center', fontWeight:700, color:'var(--red)' }}>{p.vistas}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>

        {/* ── Abandono por producto ── */}
        <div className="card" style={{ marginBottom:24 }}>
          <div className="card-header"><span className="card-title">Abandono de carrito por producto</span></div>
          <div className="card-body" style={{ padding:0 }}>
            {abandono.porProducto.length === 0 ? (
              <div style={{ padding:20, textAlign:'center', color:'var(--text-muted)', fontSize:13 }}>Sin datos</div>
            ) : (
              <table className="table" style={{ fontSize:13 }}>
                <thead><tr><th>Producto</th><th>Sesiones c/carrito</th><th>Compraron</th><th>Abandonaron</th><th>Tasa abandono</th></tr></thead>
                <tbody>
                  {abandono.porProducto.slice(0, 15).map(p => (
                    <tr key={p.producto_id}>
                      <td style={{ fontWeight:600 }}>{p.producto_nombre || p.producto_id}</td>
                      <td style={{ textAlign:'center' }}>{p.sesiones_carrito}</td>
                      <td style={{ textAlign:'center', fontWeight:700, color:'var(--green)' }}>{p.compraron}</td>
                      <td style={{ textAlign:'center', fontWeight:700, color:'var(--red)' }}>{p.abandonaron}</td>
                      <td style={{ textAlign:'center', fontWeight:700, color: p.tasa_abandono > 50 ? 'var(--red)' : 'var(--text-muted)' }}>{p.tasa_abandono}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* ── Ubicación y dispositivos ── */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, marginBottom:24 }}>
          {/* Países y ciudades */}
          <div className="card">
            <div className="card-header"><span className="card-title">Ubicación de visitantes</span></div>
            <div className="card-body" style={{ padding:0 }}>
              {demografia.paises.length === 0 ? (
                <div style={{ padding:20, textAlign:'center', color:'var(--text-muted)', fontSize:13 }}>Sin datos de ubicación aún</div>
              ) : (
                <>
                  <table className="table" style={{ fontSize:13 }}>
                    <thead><tr><th>País</th><th>Sesiones</th></tr></thead>
                    <tbody>
                      {demografia.paises.map(p => (
                        <tr key={p.pais}>
                          <td style={{ fontWeight:600 }}>{PAIS_NOMBRE(p.pais)}</td>
                          <td style={{ textAlign:'center', fontWeight:700, color:'var(--green)' }}>{p.sesiones}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {demografia.ciudades.length > 0 && (
                    <div style={{ padding:'12px 16px', borderTop:'1px solid var(--border)' }}>
                      <div style={{ fontSize:11, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:0.5, marginBottom:8 }}>Top ciudades</div>
                      {demografia.ciudades.map(c => (
                        <div key={c.ciudad + c.pais} style={{ display:'flex', justifyContent:'space-between', fontSize:13, padding:'4px 0' }}>
                          <span>{c.ciudad} <span style={{ color:'var(--text-muted)', fontSize:11 }}>({PAIS_NOMBRE(c.pais)})</span></span>
                          <span style={{ fontWeight:700 }}>{c.sesiones}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Dispositivos, navegadores, SO */}
          <div className="card">
            <div className="card-header"><span className="card-title">Dispositivos y navegadores</span></div>
            <div className="card-body">
              {demografia.dispositivos.length === 0 ? (
                <div style={{ textAlign:'center', color:'var(--text-muted)', fontSize:13, padding:8 }}>Sin datos aún</div>
              ) : (
                <>
                  <div style={{ display:'flex', gap:12, marginBottom:16, flexWrap:'wrap' }}>
                    {demografia.dispositivos.map(d => (
                      <div key={d.dispositivo} style={{ flex:1, minWidth:90, textAlign:'center', padding:'14px 8px', background:'rgba(28,58,30,0.04)', borderRadius:10 }}>
                        <div style={{ fontSize:24, marginBottom:4 }}>{DISPOSITIVO_ICONO(d.dispositivo)}</div>
                        <div style={{ fontWeight:900, fontSize:18, color:'var(--forest)' }}>{d.sesiones}</div>
                        <div style={{ fontSize:11, color:'var(--text-muted)' }}>{DISPOSITIVO_LABEL(d.dispositivo)}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
                    <div>
                      <div style={{ fontSize:11, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:0.5, marginBottom:8 }}>Navegador</div>
                      {demografia.navegadores.map(n => (
                        <div key={n.navegador} style={{ display:'flex', justifyContent:'space-between', fontSize:13, padding:'3px 0' }}>
                          <span>{n.navegador}</span><span style={{ fontWeight:700 }}>{n.sesiones}</span>
                        </div>
                      ))}
                    </div>
                    <div>
                      <div style={{ fontSize:11, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:0.5, marginBottom:8 }}>Sistema</div>
                      {demografia.so.map(s => (
                        <div key={s.so} style={{ display:'flex', justifyContent:'space-between', fontSize:13, padding:'3px 0' }}>
                          <span>{s.so}</span><span style={{ fontWeight:700 }}>{s.sesiones}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* ── Fuentes de tráfico y campañas ── */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, marginBottom:24 }}>
          <div className="card">
            <div className="card-header"><span className="card-title">Fuentes de tráfico</span></div>
            <div className="card-body" style={{ padding:0 }}>
              {origenes.fuentes.length === 0 ? (
                <div style={{ padding:20, textAlign:'center', color:'var(--text-muted)', fontSize:13 }}>Sin datos aún</div>
              ) : (
                <table className="table" style={{ fontSize:13 }}>
                  <thead><tr><th>Fuente</th><th>Sesiones</th><th>Compras</th></tr></thead>
                  <tbody>
                    {origenes.fuentes.map(f => (
                      <tr key={f.fuente}>
                        <td style={{ fontWeight:600 }}>{FUENTE_LABEL(f.fuente)}</td>
                        <td style={{ textAlign:'center', fontWeight:700, color:'var(--green)' }}>{f.sesiones}</td>
                        <td style={{ textAlign:'center', fontWeight:700 }}>{f.compras}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-header"><span className="card-title">Campañas (UTM)</span></div>
            <div className="card-body" style={{ padding:0 }}>
              {origenes.campanas.length === 0 ? (
                <div style={{ padding:20, textAlign:'center', color:'var(--text-muted)', fontSize:13 }}>Sin campañas con parámetros UTM detectadas</div>
              ) : (
                <table className="table" style={{ fontSize:13 }}>
                  <thead><tr><th>Campaña</th><th>Origen / Medio</th><th>Sesiones</th><th>Compras</th></tr></thead>
                  <tbody>
                    {origenes.campanas.map((c,i) => (
                      <tr key={i}>
                        <td style={{ fontWeight:600 }}>{c.utm_campaign}</td>
                        <td style={{ fontSize:12, color:'var(--text-muted)' }}>{c.utm_source} / {c.utm_medium}</td>
                        <td style={{ textAlign:'center', fontWeight:700, color:'var(--green)' }}>{c.sesiones}</td>
                        <td style={{ textAlign:'center', fontWeight:700 }}>{c.compras}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
        </>
        )}
      </div>
    </div>
  );
}
